<script setup>
import { BRAKE_START, SMOKE_CLEAR_START } from '../utils/timing'

// The car slams on its brakes and the smoke comes from its tyres and exhaust: tiny near the ground, rising and growing into
// a big cloud. `at` is when a puff starts, as a fraction of the time between braking and standing still. Together the
// clouds cover the number, then clear from left to right.
const EMIT_START = BRAKE_START + 0.09
const EMIT_SPAN = 0.74
const puffs = [
  { x: 77, at: 0, driftX: 12 },
  { x: 50, at: 0.05, driftX: -3 },
  { x: 66, at: 0.24, driftX: 4 },
  { x: 34, at: 0.4, driftX: -14 },
  { x: 58, at: 0.62, driftX: 3 },
  { x: 42, at: 0.81, driftX: -9 },
  { x: 72, at: 1, driftX: 10 },
].map((puff, index) => {
  const delay = EMIT_START + puff.at * EMIT_SPAN
  return {
    ...puff,
    x: 50 + (puff.x - 50) / 2,
    y: 22.5,
    size: 9 + (index % 3),
    delay,
    duration: SMOKE_CLEAR_START + puff.x * 0.012 - delay,
    driftX: `${puff.driftX / 2}cqw`,
    driftY: '-10cqw',
  }
})
</script>

<template>
  <div class="thx-smoke" aria-hidden="true">
    <svg width="0" height="0" focusable="false">
      <filter id="thx-smoke" x="-20%" y="-20%" width="140%" height="140%">
        <feTurbulence type="fractalNoise" baseFrequency="0.024" numOctaves="3" seed="4" result="noise" />
        <feDisplacementMap in="SourceGraphic" in2="noise" scale="30" />
      </filter>
    </svg>

    <div class="thx-smoke__layer">
      <span
        v-for="(puff, index) in puffs"
        :key="index"
        class="thx-smoke__puff"
        :style="{
          left: `${puff.x - puff.size / 2}cqw`,
          top: `${puff.y - puff.size / 2}cqw`,
          width: `${puff.size}cqw`,
          height: `${puff.size}cqw`,
          '--delay': `${puff.delay}s`,
          '--duration': `${puff.duration}s`,
          '--drift-x': puff.driftX,
          '--drift-y': puff.driftY,
        }"
      />
    </div>
  </div>
</template>

<style scoped>
.thx-smoke {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
}

.thx-smoke__layer {
  position: absolute;
  inset: 0;
  filter: url(#thx-smoke) blur(0.25cqw);
}

.thx-smoke__puff {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgb(246 238 238) 0%,
    rgb(246 232 232) 40%,
    rgb(240 215 215 / 85%) 58%,
    rgb(240 200 200 / 0%) 72%
  );
  opacity: 0;
  animation: thx-smoke-puff var(--duration) ease-out var(--delay) both;
}

/* Starts tiny at the exhaust pipe, rises quickly and grows into a big cloud. */
@keyframes thx-smoke-puff {
  0% {
    opacity: 0;
    scale: 0.12;
    translate: 0 0;
  }

  12% {
    opacity: 1;
  }

  40% {
    scale: 1.6;
    translate: calc(var(--drift-x) * 0.7) calc(var(--drift-y) * 0.7);
  }

  70% {
    opacity: 1;
    scale: 2.2;
  }

  100% {
    opacity: 0;
    scale: 2.6;
    translate: var(--drift-x) var(--drift-y);
  }
}

@media (prefers-reduced-motion: reduce) {
  .thx-smoke {
    display: none;
  }
}
</style>
